import * as React from 'react';
import './pdf.css';
import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';

import { Bloodwork } from '../models/Bloodwork';
import { Button, Card, Image } from 'react-bootstrap';

import { BloodworkAnalysis } from '../models/BloodworkAnalysis';
import { buildSystemGroupDefs, buildAttrToSystem, groupBySystem as groupMarkersBySystem } from '../models/mooSystemGrouping';
import { PanelField } from '../models/PanelField';
import Moment from 'react-moment';
import moment from 'moment';
import { FurtherConsiderations, Gender, Menopause, PanelMarker, Section } from '../models';
import { DisclaimerSection } from '../components/bloodworkz/disclaimer-section';
import { PDFSection } from '../components/bloodworkz/pdf-section';
import { SummaryDashboardSection } from '../components/bloodworkz/summary-dashboard-section';
import { ResultsOverviewPanel, isHdlHighSoleOutOfOptimal, isGlobulinOptimal, isImmuneAbsolute, keepInInflammationPanel, keepInImmunePanel, suppressAlpWhenZincSupersedes } from '../components/bloodworkz/results-overview-panel';
import { SupplementsSummaryPanel } from '../components/bloodworkz/supplements-summary-panel';
import { BMIBarChart } from '../components/charts/BMIBarChart';
import { MathUtils } from '../utils';
import { computePdfSectionPacking } from '../utils/pdfSectionPacking';
import { MooPanel, MooPanelSlice, packMooColumn } from '../utils/pdfMooPagination';
import Storage from '@aws-amplify/storage';

const logo = require('../assets/imgs/logo.png');
const html2pdf = require('html2pdf.js');

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

interface BloodworkAnalysisPDFContainerProps {
  bloodworkEntryStore?: BloodworkEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  withNotes: boolean;
  bloodworkAnalysis: BloodworkAnalysis;
  previousBloodwork?: Bloodwork;
  furtherConsiderations: FurtherConsiderations;
  headerLogoDataUrl: string | null;
  footerBrandDataUrl: string | null;
  isPdfReady: boolean;
  preparedPdf: any | null;
  // The set of sections the user excluded for this export, written to
  // sessionStorage by the parent window. Empty means everything is included.
  excludedFromPdfSections: string[];
  showOnlySectionSignals: boolean;
  showOnlyImmuneSectionSignals: boolean;
  showImmuneAbsolutes: boolean;
  // Per-export override for "Markers Outside Optimal" bar graphs, mirrored from
  // the on-screen section-header checkbox. `null` → no override, fall back to
  // the saved `markersOutsideOptimalDisplay` profile preference.
  showBarsInMooOverride: boolean | null;
};

@inject(
  'bloodworkEntryStore',
  'furtherConsiderationsEntryStore',
  'userProfileUIStore',
)
@observer
export class BloodworkAnalysisPDFContainer extends React.Component<
  BloodworkAnalysisPDFContainerProps,
  State
> {
  state: State = {
    withNotes: false,
    bloodworkAnalysis: new BloodworkAnalysis(),
    previousBloodwork: undefined,
    furtherConsiderations: new FurtherConsiderations(),
    headerLogoDataUrl: null,
    footerBrandDataUrl: null,
    isPdfReady: false,
    preparedPdf: null,
    excludedFromPdfSections: [],
    showOnlySectionSignals: true,
    showOnlyImmuneSectionSignals: true,
    showImmuneAbsolutes: true,
    showBarsInMooOverride: null,
  };

  // Kept on the instance (not state) so the Results Overview panel can show
  // trend arrows for sections that have prior-test data.
  private previousBloodworkAnalysis?: BloodworkAnalysis;

  // --- Helpers ---------------------------------------------------------------

  // Turn any URL into a data URL (so html2canvas can render it reliably)
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // Resolve the HEADER logo (custom if present; otherwise bundled default) to a data URL
  private resolveHeaderLogoDataUrl = async (): Promise<string> => {
    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    if (candidate === logo) {
      return await this.toDataUrl(candidate);
    }

    // 1) If getLogoUrl() returns http(s), try directly
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        return await this.toDataUrl(candidate);
      } catch {
        /* fall through */
      }
    }

    // 2) Try S3: user-logos/<filename>
    try {
      const key = (candidate || '').toString().split('/').pop();
      if (key) {
        const s3Url = await Storage.get(`user-logos/${key}`);
        return await this.toDataUrl(s3Url as string);
      }
    } catch {
      /* fall through */
    }

    // 3) Fallback: bundled default
    const bundled =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(bundled);
  };

  // Resolve the FOOTER brand mark (usually the bundled logo) to a data URL
  private resolveFooterBrandDataUrl = async (): Promise<string> => {
    const brandUrl =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(brandUrl);
  };

  // Prepare logos and PDF data in advance
  private preparePdfAssets = async (): Promise<void> => {
    try {
      // Load logos in parallel
      const [headerLogoDataUrl, footerBrandDataUrl] = await Promise.all([
        this.resolveHeaderLogoDataUrl(),
        this.resolveFooterBrandDataUrl(),
      ]);

      // NOTE: we intentionally do NOT set isPdfReady here. Keeping it false
      // until preparePdf() actually finishes means the "Preparing your
      // report..." text stays up continuously until the Export button is truly
      // ready to show. Previously isPdfReady flipped true at this point (after
      // the logos loaded but before the PDF was generated), so the text
      // vanished and left a multi-second gap with no visible button while
      // html2pdf ran.
      this.setState({
        headerLogoDataUrl,
        footerBrandDataUrl,
      });

      // Update the logo in DOM immediately for better UX
      const logoElement = document.getElementById('logo') as HTMLImageElement;
      if (logoElement && headerLogoDataUrl) {
        logoElement.src = headerLogoDataUrl;
      }

      // Now prepare the actual PDF
      await this.preparePdf();
    } catch (error) {
      console.error('Error preparing PDF assets:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // Prepare the actual PDF object in advance
  private preparePdf = async (): Promise<void> => {
    try {
      const { bloodworkAnalysis, headerLogoDataUrl, footerBrandDataUrl } =
        this.state;

      if (!bloodworkAnalysis.bloodwork) {
        console.error('Bloodwork analysis not ready');
        return;
      }

      const firstInitialOfFirstName =
        bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
      const lastName = bloodworkAnalysis.bloodwork.client.lastName;
      const collectionDate = moment(
        bloodworkAnalysis.bloodwork.collectionDate,
      ).format('YYYY-MM-DD');
      const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

      const rootEl = document.getElementById('test-results-analysis-panel');
      const pdfExportButton = document.getElementById('pdf-export-button');
      const cardHeaders = document.getElementsByClassName('card-header');

      if (pdfExportButton) pdfExportButton.style.opacity = '0';

      if (cardHeaders) {
        for (let i = 0; i < cardHeaders.length; i++) {
          (cardHeaders[i] as HTMLElement).style.marginTop = '-1px';
        }
      }

      if (!rootEl) {
        console.error('PDF root element not found');
        return;
      }

      // Helper: detect 'PNG' or 'JPEG' from data URL
      const detectType = (dataUrl: string): 'PNG' | 'JPEG' => {
        return dataUrl.startsWith('data:image/jpeg') ||
          dataUrl.startsWith('data:image/jpg')
          ? 'JPEG'
          : 'PNG';
      };
      const footerImgType = footerBrandDataUrl
        ? detectType(footerBrandDataUrl)
        : 'PNG';

      const opt = {
        margin: [8, -2, 5, 0],
        filename,
        image: { type: 'jpeg', quality: 1 },
        jsPDF: { orientation: 'p' },
        pagebreak: { mode: ['css', 'legacy'], avoid: ['.disclaimer-panel', '.pdf-section-with-notes', '.pdf-glucose-bar-graphs'] },
        html2canvas: {
          scale: 1,
          useCORS: true,
          allowTaint: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (doc: Document) => {
            const img = doc.getElementById('logo') as HTMLImageElement | null;
            if (img && headerLogoDataUrl) img.src = headerLogoDataUrl;
          },
        },
      };

      // Generate PDF and add footer
      const preparedPdf = await html2pdf()
        .set(opt)
        .from(rootEl)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          // Only add footer if we have the brand data URL
          if (footerBrandDataUrl) {
            // Compute brand logo aspect ratio from the data URL
            const props = pdf.getImageProperties(footerBrandDataUrl);
            const naturalRatio = props ? props.width / props.height : 5.5; // fallback ratio

            const footerFontSize = 8;
            const logoHeight = 3.8; // tweak to taste
            const logoWidth = logoHeight * naturalRatio;

            const rightText = 'Powered by';
            const leftPadding = 8;
            const rightPadding = 8;
            const bottomPadding = 6;
            const gap = 2;

            const fullName = `${bloodworkAnalysis.bloodwork.client.firstName} ${bloodworkAnalysis.bloodwork.client.lastName}`;
            const formattedDate = moment(
              bloodworkAnalysis.bloodwork.collectionDate,
            ).format('MM/DD/YYYY');

            // Add Prepared By if available
            const { userProfileUIStore } = this.props;
            const preparedBy =
              userProfileUIStore?.currentUserProfile?.preparedBy;

            let leftBase = `Prepared for: ${fullName}  •  Collection Date: ${formattedDate}`;
            if (preparedBy) {
              leftBase += `  •  Prepared by: ${preparedBy}`;
            }

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              const baselineY = pageHeight - bottomPadding;

              pdf.setFontSize(footerFontSize);

              // Right block width
              const rightTextWidth = pdf.getTextWidth(rightText);
              const rightBlockWidth = rightTextWidth + gap + logoWidth;
              const rightStartX = pageWidth - rightPadding - rightBlockWidth;

              // Fit left text to avoid overlap
              const maxLeftWidth = rightStartX - leftPadding - 4;
              const fit = (s: string) => {
                if (pdf.getTextWidth(s) <= maxLeftWidth) return s;
                let t = s;
                while (t.length > 4 && pdf.getTextWidth(t + '…') > maxLeftWidth)
                  t = t.slice(0, -1);
                return t + '…';
              };
              const leftText = fit(leftBase);

              // Draw left and right blocks
              pdf.text(leftText, leftPadding, baselineY);
              pdf.text(rightText, rightStartX, baselineY);
              pdf.addImage(
                footerBrandDataUrl,
                footerImgType,
                rightStartX + rightTextWidth + gap,
                baselineY - logoHeight + 0.8,
                logoWidth,
                logoHeight,
              );
            }
          }

          return pdf;
        });

      this.setState({
        preparedPdf,
        isPdfReady: true,
      });

      // Show button only when PDF is ready
      this.showOrHidePdfExportButton(true);
      if (pdfExportButton) pdfExportButton.style.opacity = '1';
    } catch (error) {
      console.error('Error preparing PDF:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // --------------------------------------------------------------------------

  componentDidMount() {
    this.showOrHidePdfExportButton(false);

    const withNotes = sessionStorage.getItem('exportWithNotes') === 'true';
    const bloodworkJSON = sessionStorage.getItem('bloodwork');
    const previousBloodworkJSON = sessionStorage.getItem('previousBloodwork');
    const excludedFromPdfSectionsJSON = sessionStorage.getItem('excludedFromPdfSections');
    const excludedFromPdfSections: string[] = excludedFromPdfSectionsJSON
      ? JSON.parse(excludedFromPdfSectionsJSON)
      : [];
    const showOnlySectionSignalsRaw = sessionStorage.getItem('showOnlySectionSignals');
    // Default to true (matches UserProfile default) if the key is missing,
    // e.g. when this window is reopened independently of the export flow.
    const showOnlySectionSignals =
      showOnlySectionSignalsRaw === null ? true : showOnlySectionSignalsRaw === 'true';
    const showOnlyImmuneSectionSignalsRaw = sessionStorage.getItem('showOnlyImmuneSectionSignals');
    const showOnlyImmuneSectionSignals =
      showOnlyImmuneSectionSignalsRaw === null ? true : showOnlyImmuneSectionSignalsRaw === 'true';
    const showImmuneAbsolutesRaw = sessionStorage.getItem('showImmuneAbsolutes');
    const showImmuneAbsolutes =
      showImmuneAbsolutesRaw === null ? true : showImmuneAbsolutesRaw === 'true';
    const showBarsInMooRaw = sessionStorage.getItem('showBarsInMoo');
    const showBarsInMooOverride: boolean | null =
      showBarsInMooRaw === null ? null : showBarsInMooRaw === 'true';
    sessionStorage.clear();

    // Ensure profile is loaded (for custom logo)
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore && !userProfileUIStore.currentUserProfile) {
      userProfileUIStore.loadCurrentUserProfile();
    }

    this.props.bloodworkEntryStore
      ?.loadBloodworkRanges()
      .then(bloodworkRanges => {
        const bloodwork = new Bloodwork(JSON.parse(bloodworkJSON!));
        const previousBloodwork = previousBloodworkJSON
          ? new Bloodwork(JSON.parse(previousBloodworkJSON))
          : undefined;

        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init().performAnalysis();

        if (previousBloodwork && previousBloodwork.collectionDate) {
          const previousBloodworkAnalysis = new BloodworkAnalysis({
            bloodwork: previousBloodwork,
            bloodworkRanges,
            bloodworkFields: PanelField.ALL_FIELDS,
          });
          previousBloodworkAnalysis.init();
          bloodworkAnalysis.preparePreviousPanelMarkers(
            previousBloodworkAnalysis,
          );
          this.previousBloodworkAnalysis = previousBloodworkAnalysis;
        } else {
          this.previousBloodworkAnalysis = undefined;
        }
        return { bloodworkAnalysis, previousBloodwork };
      })
      .then(({ bloodworkAnalysis, previousBloodwork }) => {
        return this.props
          .furtherConsiderationsEntryStore!.loadFurtherConsiderations()
          .then(furtherConsiderations => ({
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
          }));
      })
      .then(
        ({ bloodworkAnalysis, previousBloodwork, furtherConsiderations }) => {
          this.setState({
            withNotes,
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
            excludedFromPdfSections,
            showOnlySectionSignals,
            showOnlyImmuneSectionSignals,
            showImmuneAbsolutes,
            showBarsInMooOverride,
          });
        },
      );
  }

  componentDidUpdate(_prevProps: any, prevState: State) {
    if (
      !Object.values(prevState.bloodworkAnalysis).filter(Boolean).length &&
      Object.values(this.state.bloodworkAnalysis).filter(Boolean).length
    ) {
      setTimeout(() => this.preparePdfAssets(), 2000);
    }
  }

  componentWillUnmount() {
    sessionStorage.clear();
  }

  componentWillReceiveProps() {
    this.scrollTop();
  }

  scrollTop() {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo(0, 0);
    }
  }

  showOrHidePdfExportButton(show: boolean, timeout: number = 0) {
    setTimeout(() => {
      const opacity = show ? '1' : '0';
      const pdfExportButton = document.getElementById('pdf-export-button');
      if (pdfExportButton) {
        pdfExportButton.style.opacity = opacity;
      }
    }, timeout);
  }

  getLogoUrl = (): string => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;
    return (
      userProfile?.hasCustomLogo() ? userProfile.customLogoUrl : logo
    ) as any;
  };

  // Legacy helper still used elsewhere
  getImageAsDataUrl = async (url: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // ========= EXPORT =========
  export = async () => {
    const { preparedPdf, isPdfReady, bloodworkAnalysis } = this.state;
    const firstInitialOfFirstName =
      bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
    const lastName = bloodworkAnalysis.bloodwork.client.lastName;
    const collectionDate = moment(
      bloodworkAnalysis.bloodwork.collectionDate,
    ).format('YYYY-MM-DD');
    const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

    if (preparedPdf && isPdfReady) {
      preparedPdf.save(filename);
    } else {
      // Fallback: prepare PDF on the fly (shouldn't happen normally)
      console.log('PDF not prepared, falling back to on-demand generation...');
      await this.preparePdf();
      if (this.state.preparedPdf) {
        this.state.preparedPdf.save(filename);
      }
    }
  };

  pageBreakClassName(panelMarkers: PanelMarker[]): string {
    return panelMarkers.length > 0 ? 'html2pdf__page-break' : '';
  }

  render() {
    const { bloodworkAnalysis, furtherConsiderations, isPdfReady, excludedFromPdfSections, showOnlySectionSignals, showOnlyImmuneSectionSignals, showImmuneAbsolutes } = this.state;
    if (!bloodworkAnalysis.bloodwork) {
      return null;
    }
    // "Excluded from PDF" is the new per-export gate. UI collapse no longer
    // affects what ends up in the PDF; the "Include in PDF" checkbox in each
    // section header is the source of truth. Aliased as isCollapsed to keep
    // the call sites below unchanged.
    const isCollapsed = (section: Section) => excludedFromPdfSections.includes(section);
    const today = new Date().toISOString();

    // Only show Immune System Analysis if WBC has a value
    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // Clean leading blank lines/spaces from the global Notes block
    const notesObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
      n => n.section === Section.NOTES,
    );
    const globalNotesText = (notesObj?.text ?? '').trimStart();

    // Split Hormones section if there are more than 14 markers
    const sexHormoneMarkers = bloodworkAnalysis.getSexHormonePanelMarkers();
    const hormonesFirstPageMarkers = sexHormoneMarkers.slice(0, 13);
    const hormonesSecondPageMarkers = sexHormoneMarkers.slice(13);

    // Split Nutrients section if there are more than 14 markers
    const mineralMarkers = bloodworkAnalysis.getMineralPanelMarkers();
    const nutrientsFirstPageMarkers = mineralMarkers.slice(0, 13);
    const nutrientsSecondPageMarkers = mineralMarkers.slice(13);

    // Collect ALL markers from every section for the summary views.
    // We use a Set to deduplicate by object identity (same PanelMarker instance
    // may appear in multiple sections, e.g. globulin appears in WBC and Proteins).
    const allMarkerSet = new Set<PanelMarker>([
      ...bloodworkAnalysis.getRBCPanelMarkers(),
      ...bloodworkAnalysis.getIronPanelMarkers(),
      ...bloodworkAnalysis.getWBCPanelMarkers(),
      ...bloodworkAnalysis.getPlateletPanelMarkers(),
      ...bloodworkAnalysis.getGlucosePanelMarkers(),
      ...bloodworkAnalysis.getKidneyPanelMarkers(),
      ...bloodworkAnalysis.getProteinsPanelMarkers(),
      ...bloodworkAnalysis.getMineralPanelMarkers(),
      ...bloodworkAnalysis.getElectrolytePanelMarkers(),
      ...bloodworkAnalysis.getLiverPanelMarkers(),
      ...bloodworkAnalysis.getLipidsPanelMarkers(),
      ...bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
      ...bloodworkAnalysis.getThyroidPanelMarkers(),
      ...bloodworkAnalysis.getStressAdrenalPanelMarkers(),
      ...bloodworkAnalysis.getSexHormonePanelMarkers(),
      ...bloodworkAnalysis.getProstatePanelMarkers(),
    ]);
    const allMarkers = Array.from(allMarkerSet);

    // "Higher is better" markers (eGFR, PSA Free, PSA % Free) have no upper alarm bound
    // and should never appear in Above Optimal.
    const isHigherIsBetter = (pm: PanelMarker) =>
      pm.isEGFR?.() || pm.field?.useEGFRChart;

    // RDW-chart markers render everything below optimalMax as green — low values are fine,
    // so they must never appear in Below Optimal.
    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;

    // For "higher is better" markers, alarmMin/labMin can be 0 (clamped from a negative
    // computed value), making isLessThanAlarm/isLessThanLab unreliable. Compare directly
    // against the stored range values and only use a threshold when it is > 0.
    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    // Zone-penetration score: fraction of the relevant danger zone consumed.
    // WBC uses its own 9-zone percentFromMean which is already zone-normalized.
    // Falls back to |percentFromMean| when denominators collapse to zero.
    const zonePenetrationScore = (pm: PanelMarker, section: 'alarm' | 'lab' | 'optimal'): number => {
      const r = pm.range;
      if (!r) return Math.abs(pm.percentFromMean ?? 0);
      if (pm.field?.useWBCPercentFromMean) return Math.abs(pm.percentFromMean ?? 0);
      const v = pm.value;
      if (section === 'alarm') {
        const denom = r.stdDev && r.stdDev > 0 ? r.stdDev : 1;
        if (v > r.alarmMax) return (v - r.alarmMax) / denom;
        if (r.alarmMin > 0 && v < r.alarmMin) return (r.alarmMin - v) / denom;
        return 0;
      }
      if (section === 'lab') {
        if (v > r.labMax) {
          const denom = r.alarmMax - r.labMax;
          return denom > 0 ? (v - r.labMax) / denom : Math.abs(pm.percentFromMean ?? 0);
        }
        const denom = r.labMin - (r.alarmMin ?? 0);
        return denom > 0 ? (r.labMin - v) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      // section === 'optimal'
      if (v > (r.optimalMax ?? r.labMax)) {
        const denom = r.labMax - (r.optimalMax ?? r.labMax);
        return denom > 0 ? (v - r.optimalMax!) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      const optMin = r.optimalMin ?? r.labMin;
      const denomOpt = optMin - r.labMin;
      return denomOpt > 0 ? (optMin - v) / denomOpt : Math.abs(pm.percentFromMean ?? 0);
    };

    const alarmZoneDesc   = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'alarm')   - zonePenetrationScore(a, 'alarm');
    const labZoneDesc     = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'lab')     - zonePenetrationScore(a, 'lab');
    const optimalZoneDesc = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'optimal') - zonePenetrationScore(a, 'optimal');

    // "Alarm Range Markers" — outside alarm threshold in either direction, worst first
    const needsAttentionGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
          : (pm.isGreaterThanAlarm?.() || (!isRdwChart(pm) && pm.isLessThanAlarm?.()))
      ))
      .sort(alarmZoneDesc);

    // "Outside Lab Range Markers" — outside lab range but within alarm, worst first
    const worthDiscussingGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
          : (
              (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
              (!isRdwChart(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.())
            )
      ))
      .sort(labZoneDesc);

    // "Outside Optimal Range Markers" — outside optimal but within lab range, worst first
    const fineTuneGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
          : (
              (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
              (!isRdwChart(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.())
            )
      ))
      .sort(optimalZoneDesc);

    // Split each severity group into high vs low for the 6-panel PDF layout
    // that mirrors the UI's Markers Outside Optimal section. eGFR ("higher is
    // better") markers only flag when low, so they always land in the low half.
    const splitByDirection = (markers: PanelMarker[], side: 'alarm' | 'lab' | 'optimal') => {
      const high: PanelMarker[] = [];
      const low: PanelMarker[] = [];
      for (const pm of markers) {
        if (isHigherIsBetter(pm)) { low.push(pm); continue; }
        const isHigh =
          side === 'alarm'   ? pm.isGreaterThanAlarm?.() :
          side === 'lab'     ? pm.isGreaterThanLab?.() :
                               pm.isGreaterThanOptimal?.();
        (isHigh ? high : low).push(pm);
      }
      return { high, low };
    };
    const { high: highAlarmGroup,    low: lowAlarmGroup }    = splitByDirection(needsAttentionGroup,  'alarm');
    const { high: aboveLabGroup,     low: belowLabGroup }    = splitByDirection(worthDiscussingGroup, 'lab');
    const { high: aboveOptimalGroup, low: belowOptimalGroup } = splitByDirection(fineTuneGroup,       'optimal');

    // Greedy bin-packing: group sections onto pages to minimise blank space.
    // Each unit's height is estimated from marker count; units that fit together
    // share a page (no break between them). The last unit never gets a break-after
    // so that Insights can flow onto the same page if room remains.
    //
    // Tuning guide — increase PAGE_CAPACITY to pack more sections per page;
    // decrease it to be more conservative:
    //
    //   Constant      | Value  | Meaning
    //   --------------|--------|--------------------------------------------------
    //   PAGE_CAPACITY | 900 px | Usable vertical space per page
    //   BASE_HEIGHT   | 180 px | Fixed overhead per section (title + table header)
    //   ROW_HEIGHT    |  35 px | Height per bar-graph marker row
    //   SECTION_GAP   |  24 px | Spacing between sections sharing a page
    const PAGE_CAPACITY = 900;
    const BASE_HEIGHT   = 180;
    const ROW_HEIGHT    = 40;
    const SECTION_GAP   = 24;
    // Notes card overhead (header + padding) + per-line height
    const NOTES_OVERHEAD   = 80;
    const NOTES_LINE_HEIGHT = 22;

    // Returns estimated height of the Section Notes card for a given section,
    // or 0 when withNotes is off or the section has no notes text.
    const sectionNotesHeight = (section: Section): number => {
      if (!this.state.withNotes) return 0;
      const noteObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
        n => n.section === section,
      );
      const text = (noteObj?.text ?? '').trimStart();
      if (!text) return 0;
      return NOTES_OVERHEAD + text.split('\n').length * NOTES_LINE_HEIGHT;
    };

    const estH = (markers: PanelMarker[], section?: Section) =>
      BASE_HEIGHT + markers.length * ROW_HEIGHT + (section ? sectionNotesHeight(section) : 0);

    const glucoseDisplayMarkers = (() => {
      const all = bloodworkAnalysis.getGlucosePanelMarkers();
      return isHdlHighSoleOutOfOptimal(all)
        ? all.filter(pm => pm.attribute !== 'hdl')
        : all;
    })();

    const inflammationDisplayMarkers = (() => {
      const all = bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers();
      return showOnlySectionSignals
        ? all.filter(keepInInflammationPanel)
        : all;
    })();

    const immuneDisplayMarkers = (() => {
      const all = bloodworkAnalysis.getWBCPanelMarkers();
      const base = showImmuneAbsolutes ? all : all.filter(pm => !isImmuneAbsolute(pm));
      return showOnlyImmuneSectionSignals
        ? suppressAlpWhenZincSupersedes(base.filter(pm => !isGlobulinOptimal(pm)).filter(keepInImmunePanel), all)
        : base;
    })();

    const packUnits = [
      { id: 'IRON',            height: estH(bloodworkAnalysis.getIronPanelMarkers(), Section.IRON),                                                          visible: !isCollapsed(Section.IRON) && bloodworkAnalysis.getIronPanelMarkers().length > 0 },
      { id: 'GLUCOSE',         height: estH(glucoseDisplayMarkers, Section.GLUCOSE),                                                                         visible: !isCollapsed(Section.GLUCOSE) && glucoseDisplayMarkers.length > 0 },
      { id: 'LIPIDS',          height: estH(bloodworkAnalysis.getLipidsPanelMarkers(), Section.LIPIDS),                                                      visible: !isCollapsed(Section.LIPIDS) && bloodworkAnalysis.getLipidsPanelMarkers().length > 0 },
      { id: 'THYROID',         height: estH(bloodworkAnalysis.getThyroidPanelMarkers(), Section.THYROID),                                                    visible: !isCollapsed(Section.THYROID) && bloodworkAnalysis.getThyroidPanelMarkers().length > 0 },
      // For split sections, count notes on the first part only (notes appear after the first chunk).
      { id: 'NUTRIENTS_1',     height: estH(nutrientsFirstPageMarkers, Section.NUTRIENTS),                                                                   visible: !isCollapsed(Section.NUTRIENTS) && nutrientsFirstPageMarkers.length > 0 },
      { id: 'NUTRIENTS_2',     height: estH(nutrientsSecondPageMarkers),                                                                                     visible: !isCollapsed(Section.NUTRIENTS) && nutrientsSecondPageMarkers.length > 0 },
      { id: 'PROTEINS',        height: estH(bloodworkAnalysis.getProteinsPanelMarkers(), Section.PROTEINS),                                                  visible: !isCollapsed(Section.PROTEINS) && bloodworkAnalysis.getProteinsPanelMarkers().length > 0 },
      { id: 'INFLAMMATION',    height: estH(inflammationDisplayMarkers, Section.INFLAMMATION_OXIDATIVE_STRESS),                                              visible: !isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS) && inflammationDisplayMarkers.length > 0 },
      { id: 'PLATELETS',       height: estH(bloodworkAnalysis.getPlateletPanelMarkers(), Section.PLATELETS),                                                 visible: !isCollapsed(Section.PLATELETS) && bloodworkAnalysis.getPlateletPanelMarkers().length > 0 },
      { id: 'LIVER',           height: estH(bloodworkAnalysis.getLiverPanelMarkers(), Section.LIVER_BILIARY),                                                visible: !isCollapsed(Section.LIVER_BILIARY) && bloodworkAnalysis.getLiverPanelMarkers().length > 0 },
      { id: 'KIDNEY',          height: estH(bloodworkAnalysis.getKidneyPanelMarkers(), Section.KIDNEY),                                                      visible: !isCollapsed(Section.KIDNEY) && bloodworkAnalysis.getKidneyPanelMarkers().length > 0 },
      { id: 'ELECTROLYTES',    height: estH(bloodworkAnalysis.getElectrolytePanelMarkers(), Section.ELECTROLYTES),                                           visible: !isCollapsed(Section.ELECTROLYTES) && bloodworkAnalysis.getElectrolytePanelMarkers().length > 0 },
      { id: 'STRESS_ADRENAL',  height: estH(bloodworkAnalysis.getStressAdrenalPanelMarkers(), Section.STRESS_ADRENAL),                                        visible: !isCollapsed(Section.STRESS_ADRENAL) && bloodworkAnalysis.getStressAdrenalPanelMarkers().length > 0 },
      { id: 'SEX_HORMONE_1',   height: estH(hormonesFirstPageMarkers, Section.SEX_HORMONE),                                                                  visible: !isCollapsed(Section.SEX_HORMONE) && hormonesFirstPageMarkers.length > 0 },
      { id: 'SEX_HORMONE_2',   height: estH(hormonesSecondPageMarkers),                                                                                      visible: !isCollapsed(Section.SEX_HORMONE) && hormonesSecondPageMarkers.length > 0 },
      { id: 'PROSTATE',        height: estH(bloodworkAnalysis.getProstatePanelMarkers(), Section.PROSTATE),                                                  visible: !isCollapsed(Section.PROSTATE) && bloodworkAnalysis.getProstatePanelMarkers().length > 0 },
    ];

    // When WBC is visible it always occupies its own page between PROTEINS
    // and INFLAMMATION. The first visible unit AFTER PROTEINS (typically
    // INFLAMMATION) starts fresh on a new page after WBC's, so the packer
    // resets pageUsed at that point instead of running a single packing pass
    // across the WBC boundary. Iron / Glucose / Lipids each have their own
    // forced page break in the JSX (empty html2pdf__page-break markers);
    // listing them in forcedPageStartIds tells the packer to reset pageUsed
    // when it reaches each one so subsequent sections pack correctly.
    const wbcIntervenes = hasWbcValue && !isCollapsed(Section.WBC);
    const { unitBreakAfter, pageUsed: packPageUsed } = computePdfSectionPacking({
      packUnits,
      wbcIntervenes,
      preWbcBarrierId: 'PROTEINS',
      forcedPageStartIds: ['IRON', 'GLUCOSE', 'LIPIDS'],
      pageCapacity: PAGE_CAPACITY,
      sectionGap: SECTION_GAP,
    });
    let pageUsed = packPageUsed;
    const visibleUnits = packUnits.filter(u => u.visible);

    // Force-break helpers. We intentionally avoid empty `html2pdf__page-break`
    // marker divs (which create blank intermediate pages when the preceding
    // section happens to exactly fill its page) and instead set break-after
    // on the LAST visible preceding pack unit. The
    // `html2pdf__page-break` class is then applied to that unit's wrapper via
    // sectionClass(), which html2pdf-legacy treats as "break after this
    // element" — producing exactly one break between the two sections.
    const findLastVisiblePackUnitBefore = (id: string): string | null => {
      const idx = packUnits.findIndex(u => u.id === id);
      if (idx <= 0) return null;
      for (let i = idx - 1; i >= 0; i--) {
        if (packUnits[i].visible) return packUnits[i].id;
      }
      return null;
    };

    // GLUCOSE / LIPIDS both start on a fresh page via empty break markers
    // in the JSX (not via break-after on their wrappers, because those
    // sections have keep-together inside). We must explicitly suppress
    // any break-after the packer would have set on the section just before
    // each of them, otherwise we get two consecutive page breaks and a
    // blank page between them.
    const glucoseVisible = !isCollapsed(Section.GLUCOSE) && glucoseDisplayMarkers.length > 0;
    if (glucoseVisible) {
      const before = findLastVisiblePackUnitBefore('GLUCOSE');
      if (before) unitBreakAfter[before] = false;
    }
    const lipidsVisible = !isCollapsed(Section.LIPIDS) && bloodworkAnalysis.getLipidsPanelMarkers().length > 0;
    if (lipidsVisible) {
      const before = findLastVisiblePackUnitBefore('LIPIDS');
      if (before) unitBreakAfter[before] = false;
    }

    // When WBC intervenes it always starts on a fresh page. Force break-after
    // on the last visible pre-WBC pack unit (typically PROTEINS).
    if (wbcIntervenes) {
      const lastPreWbc = findLastVisiblePackUnitBefore('INFLAMMATION');
      if (lastPreWbc) unitBreakAfter[lastPreWbc] = true;
    }
    // If Insights won't fully fit in the remaining space on the last section's page, force it
    // to a fresh page. Estimate conservatively (200px card overhead + 24px/line + 50px buffer)
    // so when we say it fits, it genuinely does and html2pdf won't split the card header off.
    const insightsLineCount = this.state.withNotes && globalNotesText
      ? globalNotesText.split('\n').length
      : 0;
    const insightsEstimatedHeight = insightsLineCount > 0
      ? 200 + insightsLineCount * 24
      : 0;
    const INSIGHTS_SAFETY_BUFFER = 50;
    const insightsNeedsOwnPage =
      insightsEstimatedHeight > 0 &&
      insightsEstimatedHeight > PAGE_CAPACITY - pageUsed - INSIGHTS_SAFETY_BUFFER;

    if (insightsNeedsOwnPage && visibleUnits.length > 0) {
      unitBreakAfter[visibleUnits[visibleUnits.length - 1].id] = true;
    }

    // After placing Insights, how much space is left on that page?
    // When Insights shares the last bar-graph page the base is pageUsed + spacer + Insights.
    // When Insights is on its own fresh page the base is just its own height.
    const DISCLAIMER_ESTIMATED_HEIGHT = 350;
    const pageUsedAfterInsights = insightsEstimatedHeight > 0
      ? (insightsNeedsOwnPage
          ? insightsEstimatedHeight
          : pageUsed + 12 + insightsEstimatedHeight)
      : pageUsed;

    // Supplements (when present) always starts on its own page, so any
    // following content (the Disclaimer) starts on that same page below it.
    const supplementsIncluded =
      process.env.REACT_APP_ENV !== 'prod' &&
      process.env.REACT_APP_ENV !== 'slava' &&
      !isCollapsed(Section.SUPPLEMENTS);
    const supplementsEstimatedHeight = supplementsIncluded
      ? SupplementsSummaryPanel.estimatedPdfHeight(
          bloodworkAnalysis,
          furtherConsiderations,
          this.props.userProfileUIStore?.currentUserProfile
            ?.enabledSupplementBrands,
        )
      : 0;
    const pageUsedBeforeDisclaimer = supplementsEstimatedHeight > 0
      ? supplementsEstimatedHeight
      : pageUsedAfterInsights;
    const disclaimerNeedsOwnPage =
      DISCLAIMER_ESTIMATED_HEIGHT > PAGE_CAPACITY - pageUsedBeforeDisclaimer;

    // WBC always starts on its own page. This is now handled by the
    // unitBreakAfter override above (which sets break-after on the last
    // visible pre-WBC pack unit), rather than by an empty break div in JSX —
    // using a single mechanism avoids the blank-page issue that arose when
    // the previous section exactly filled its page.

    const sectionClass = (id: string) => unitBreakAfter[id] ? 'html2pdf__page-break' : '';

    // Build attribute → body-system label map for grouping markers in the
    // dashboard sections. Shared with the web view via mooSystemGrouping so the
    // two never drift apart.
    const attrToSystem = buildAttrToSystem(buildSystemGroupDefs(bloodworkAnalysis));

    const groupBySystem = (
      markers: PanelMarker[],
      scoreOf: (pm: PanelMarker) => number,
    ) => groupMarkersBySystem(markers, scoreOf, attrToSystem);

    return (
      <>
        {/* Status row lives OUTSIDE #test-results-analysis-panel (the element
            html2pdf captures) so the "Preparing your report..." text / Export
            button is never baked into the generated PDF. */}
        <div className="text-right" style={{ marginTop: 12, paddingRight: 10 }} id="pdf-status-row">
          {isPdfReady ? (
            <Button
              id="pdf-export-button"
              onClick={this.export}
              disabled={!isPdfReady}
              style={{ opacity: isPdfReady ? 1 : 0 }}
            >
              {isPdfReady ? 'Export to PDF' : 'Preparing PDF...'}
            </Button>
          ) : (
            <span
              style={{
                color: '#766d66',
                fontStyle: 'italic',
                fontSize: 15,
              }}
            >
              Preparing your report... the Export to PDF button will appear
              here in a few seconds.
            </span>
          )}
        </div>
      <div className="pdf-page" id="test-results-analysis-panel">

        {/* Header */}
        <div className="text-center" style={{ marginTop: 24 }}>
          <Image
            id="logo"
            src={this.getLogoUrl()}
            alt="Current Logo"
            fluid
            style={{
              maxHeight: '100px',
              maxWidth: '400px',
              objectFit: 'contain',
            }}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <h3 className="text-center">
            <strong>Blood Work Analysis Report</strong>
          </h3>
          <div style={{ height: 12 }} aria-hidden />
          <div style={{ height: 48 }} aria-hidden />
        </div>

        {/* First-page meta. No break-after — each subsequent cover-area block
            (Results Overview, MOO) and the first per-system section (RBC) carry
            their own `pageBreakBefore: 'always'`. Using break-BEFORE on each
            block instead of break-AFTER on the prior one avoids the html2pdf
            phantom-blank-page bug: a chain of `page-break-after: always`
            siblings would emit an extra blank page after the last one in the
            chain (the blank page that kept appearing between the last cover
            block and RBC). */}
        <div style={{ marginTop: 48 }}>
          <div>
            <h5>
              <strong>Prepared for</strong>
            </h5>
            <h5>
              {bloodworkAnalysis.bloodwork.client.firstName}{' '}
              {bloodworkAnalysis.bloodwork.client.lastName}
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.props.userProfileUIStore?.currentUserProfile?.preparedBy && (
              <>
                <h5>
                  <strong>Prepared by</strong>
                </h5>
                <h5>
                  {this.props.userProfileUIStore.currentUserProfile.preparedBy}
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Collection Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">
                {bloodworkAnalysis.bloodwork.collectionDate}
              </Moment>
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.state.previousBloodwork && (
              <>
                <h5>
                  <strong>Previous Collection Date</strong>
                </h5>
                <h5>
                  <Moment format="MM/DD/YYYY">
                    {this.state.previousBloodwork.collectionDate}
                  </Moment>
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Analysis Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">{today}</Moment>
            </h5>
          </div>

          {/* Results Overview — page 2, started by its own
              `pageBreakBefore: 'always'` rather than the cover-meta block's
              break-after. See the cover-meta comment above for why we use
              break-BEFORE on each block instead of break-AFTER on the prior. */}
          {(() => {
            const resultsOverviewIncluded = !isCollapsed(Section.RESULTS_OVERVIEW);
            const bmiIncluded = !isCollapsed(Section.BMI);
            if (!resultsOverviewIncluded && !bmiIncluded) return null;

            const client = bloodworkAnalysis.bloodwork.client;
            const isPregnant = client.gender === Gender.FEMALE && client.menopause === Menopause.PREGNANT;
            const bw = bloodworkAnalysis.bloodwork as any;
            const heightCm = client.height != null ? Number(client.height) : (bw.height != null ? Number(bw.height) : null);
            const weightLbs = client.weight != null ? Number(client.weight) : (bw.weight != null ? Number(bw.weight) : null);
            const bmiNumber = heightCm != null && weightLbs != null
              ? MathUtils.bmi(weightLbs, heightCm)
              : null;
            const bmiSex: 'M' | 'F' | undefined = client.gender === Gender.MALE
              ? 'M'
              : client.gender === Gender.FEMALE ? 'F' : undefined;
            const bmiAgeYears = client.ageAsOf(bloodworkAnalysis.bloodwork.collectionDate);
            const canRenderBmi = bmiIncluded
              && bmiNumber != null
              && !isPregnant
              && !(bmiAgeYears != null && bmiAgeYears < 2);

            if (!resultsOverviewIncluded && !canRenderBmi) return null;

            return (
              <div style={{ pageBreakBefore: 'always' }}>
                {resultsOverviewIncluded && (
                  <>
                    <h4 style={{ marginBottom: 32 }}><strong>Results Overview</strong></h4>
                    <ResultsOverviewPanel
                      bloodworkAnalysis={bloodworkAnalysis}
                      previousBloodworkAnalysis={this.previousBloodworkAnalysis}
                      hasWbcValue={hasWbcValue}
                      direction="column"
                      showOnlySectionSignals={showOnlySectionSignals}
                      showOnlyImmuneSectionSignals={showOnlyImmuneSectionSignals}
                    />
                  </>
                )}
                {canRenderBmi && (
                  <div style={{ marginTop: resultsOverviewIncluded ? 80 : 0 }}>
                    {/* 3 line-spaces of breathing room above the title (only
                        when the Results Overview panel sits above it). */}
                    <h4 style={{ marginTop: resultsOverviewIncluded ? 48 : 0, marginBottom: 12 }}>
                      <strong>Body Mass Index (BMI)</strong>
                    </h4>
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <BMIBarChart
                        bmi={bmiNumber!}
                        ageYears={bmiAgeYears}
                        sex={bmiSex}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Markers Outside Optimal — two stacked columns (highs left, lows
              right), pre-paginated by the column packer in
              src/utils/pdfMooPagination.ts. Tall content (e.g. 16 markers in
              one "Above Optimal Range" panel) is sliced between marker rows
              into multiple cards; the section header is repeated as
              "Markers Outside Optimal (cont.)" on each spillover page. Each
              page chunk carries `pageBreakBefore: 'always'` so the first
              chunk lands on a fresh page (matching the old MOO entry point)
              and each subsequent chunk lands on its own page. The first
              per-system section (RBC) below carries its own `html2pdf__page-break`,
              so we don't need a break-after on the last chunk. */}
          {!isCollapsed(Section.MARKERS_OUTSIDE_OPTIMAL) && (needsAttentionGroup.length > 0 ||
            worthDiscussingGroup.length > 0 || fineTuneGroup.length > 0) && (() => {
              // Mirror the UI's bar-chart view in the PDF. The on-screen
              // "Show bar graphs" checkbox is the per-export source of truth
              // (passed via sessionStorage); when missing (e.g. PDF window
              // reopened directly), fall back to the saved
              // `markersOutsideOptimalDisplay` profile preference.
              const moodPref = this.props.userProfileUIStore?.currentUserProfile?.markersOutsideOptimalDisplay ?? 'showBars';
              const showBars = this.state.showBarsInMooOverride !== null
                ? this.state.showBarsInMooOverride
                : moodPref !== 'hideBars';
              // Zone colors mirror the UI marker badges.
              const zoneColors = (s: Section): { bg: string; fg: string } => {
                if (s === Section.NEEDS_ATTENTION)  return { bg: '#FC6552', fg: '#fff' };
                if (s === Section.WORTH_DISCUSSING) return { bg: '#FFD37E', fg: '#3d2700' };
                return { bg: '#FEFA83', fg: '#2e2a00' };
              };

              type PanelDef = {
                key: string;
                title: string;
                sectionForZone: Section;
                markers: PanelMarker[];
                scoreOf: (pm: PanelMarker) => number;
              };

              const panelDefs: PanelDef[] = [
                { key: 'highAlarm',    title: 'High Alarm Range',    sectionForZone: Section.NEEDS_ATTENTION,  markers: highAlarmGroup,    scoreOf: pm => zonePenetrationScore(pm, 'alarm') },
                { key: 'aboveLab',     title: 'Above Lab Range',     sectionForZone: Section.WORTH_DISCUSSING, markers: aboveLabGroup,     scoreOf: pm => zonePenetrationScore(pm, 'lab') },
                { key: 'aboveOptimal', title: 'Above Optimal Range', sectionForZone: Section.FINE_TUNE,        markers: aboveOptimalGroup, scoreOf: pm => zonePenetrationScore(pm, 'optimal') },
                { key: 'lowAlarm',     title: 'Low Alarm Range',     sectionForZone: Section.NEEDS_ATTENTION,  markers: lowAlarmGroup,     scoreOf: pm => zonePenetrationScore(pm, 'alarm') },
                { key: 'belowLab',     title: 'Below Lab Range',     sectionForZone: Section.WORTH_DISCUSSING, markers: belowLabGroup,     scoreOf: pm => zonePenetrationScore(pm, 'lab') },
                { key: 'belowOptimal', title: 'Below Optimal Range', sectionForZone: Section.FINE_TUNE,        markers: belowOptimalGroup, scoreOf: pm => zonePenetrationScore(pm, 'optimal') },
              ];

              // Pagination constants — letter portrait, 96dpi-ish. Tuned with
              // safety margin so estimates round up (we'd rather leave whitespace
              // at a page bottom than overflow). If MOO consistently leaves too
              // much whitespace, raise PAGE_CAPACITY; if cards still get clipped,
              // raise the per-element overheads.
              const PAGE_CAPACITY  = 900;          // usable height after section h4 (raised from 880: a single 80px bar + 32px system header overshot by only 8-16px, spilling lone markers like RDW/MCHC onto an extra page)
              const CARD_OVERHEAD  = 96;           // .card-header + .card-body padding + borders
              const GROUP_OVERHEAD = 32;           // .summary-system-header + group margin
              const MARKER_H       = showBars ? 80 : 32;
              const COLUMN_GAP     = 8;

              const toMooPanel = (def: PanelDef): MooPanel => ({
                key: def.key,
                title: def.title,
                sectionForZone: def.sectionForZone,
                groups: groupBySystem(def.markers, def.scoreOf),
              });

              const leftPanels  = [panelDefs[0], panelDefs[1], panelDefs[2]]
                .filter(p => p.markers.length > 0).map(toMooPanel);
              const rightPanels = [panelDefs[3], panelDefs[4], panelDefs[5]]
                .filter(p => p.markers.length > 0).map(toMooPanel);

              const packArgs = {
                pageCapacity: PAGE_CAPACITY,
                cardOverhead: CARD_OVERHEAD,
                groupOverhead: GROUP_OVERHEAD,
                markerHeight: MARKER_H,
                columnGap: COLUMN_GAP,
              };
              const leftPages  = packMooColumn({ panels: leftPanels,  ...packArgs });
              const rightPages = packMooColumn({ panels: rightPanels, ...packArgs });
              const totalChunks = Math.max(leftPages.length, rightPages.length);

              const renderSlice = (slice: MooPanelSlice) => {
                const colors = zoneColors(slice.sectionForZone);
                // Show the range's TOTAL marker count, even when the panel
                // is split across pages — not the per-page subset count.
                const sliceMarkerCount = slice.totalMarkerCount;
                return (
                  <Card
                    key={slice.key}
                    className="summary-section-card"
                    style={{ borderColor: colors.bg }}
                  >
                    <Card.Header
                      style={{
                        backgroundImage: 'none',
                        backgroundColor: colors.bg,
                        color: colors.fg,
                        borderBottomColor: colors.bg,
                      }}
                    >
                      {slice.title}&nbsp;({sliceMarkerCount})
                    </Card.Header>
                    <Card.Body>
                      <SummaryDashboardSection
                        title=""
                        section={slice.sectionForZone}
                        groups={slice.groups}
                        showCharts={showBars}
                        chartsBelow={true}
                      />
                    </Card.Body>
                  </Card>
                );
              };

              const renderColumn = (slices: MooPanelSlice[]) => (
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {slices.map(s => renderSlice(s))}
                </div>
              );

              return Array.from({ length: totalChunks }).map((_, i) => {
                const left  = leftPages[i]  ?? [];
                const right = rightPages[i] ?? [];
                if (left.length === 0 && right.length === 0) return null;
                return (
                  <div key={`moo-chunk-${i}`} style={{ pageBreakBefore: 'always' }}>
                    {/* Top buffer so html2pdf doesn't clip the page header when
                        the chunk lands on a fresh page (break-before). This h4
                        is a plain page header (no .pdf-section-title), so it
                        relies on this spacer rather than the title padding the
                        per-section titles use. */}
                    <div style={{ height: 16 }} aria-hidden />
                    {/* Constrain the MOO section to the PDF page content width
                        (matches the per-section bar graphs' .ref-range-analysis-panel,
                        which is a fixed 760px). The mini bar-graph charts are
                        Highcharts SVGs sized to their container width AT MOUNT.
                        On screen this preview panel is full browser width, but
                        html2pdf clones it into a container forced to the ~801px
                        page width — so without a fixed width the columns are wide
                        in the preview and narrow in the clone. Highcharts' SVG
                        keeps its wide preview pixel width, overflows the narrow
                        clone column, and the right side of each chart (red + half
                        the orange band) is clipped. Pinning the section to 760px
                        makes the columns identical in the preview and the clone,
                        so the charts render at the right width everywhere and the
                        drop markers stay aligned. */}
                    <div style={{ maxWidth: 760, margin: '0 auto' }}>
                      {/* 24px (not 12) because the .pdf-page > div > h4 rule
                          in pdf.css that adds the extra header gap no longer
                          matches this h4 now that it's nested inside the
                          fixed-width wrapper — keep the spacing inline. */}
                      <h4 style={{ marginBottom: 24 }}>
                        <strong>Markers Outside Optimal{i > 0 ? ' (cont.)' : ''}</strong>
                      </h4>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        {renderColumn(left)}
                        {renderColumn(right)}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          {/* Per-system sections. RBC starts on a fresh page via an empty
              `html2pdf__page-break` marker (legacy mode) rather than an inline
              `pageBreakBefore` (CSS mode). With consecutive siblings each
              carrying `pageBreakBefore: 'always'` (Results Overview, MOO, RBC),
              html2pdf was emitting a phantom blank page after the last cover
              block. The marker pattern matches what IRON/GLUCOSE/LIPIDS use
              below and avoids the conflict. The title's own padding-top
              (.pdf-section-title) buffers it from the page edge so html2pdf
              doesn't clip the top of the text. */}
          {!isCollapsed(Section.RBC) && bloodworkAnalysis.getRBCPanelMarkers().length > 0 && (
            <>
              <div className="html2pdf__page-break" />
              <div>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Blood Oxygen & Red Blood Cell Function"
                  section={Section.RBC}
                  panelMarkers={bloodworkAnalysis.getRBCPanelMarkers()}
                />
              </div>
            </>
          )}
          {/* Empty break marker AFTER RBC ensures Iron Status starts at the
              top of a fresh page. We use a marker (rather than a break-after
              class on RBC's wrapper) because RBC's bar-graphs wrapper inside
              has page-break-inside: avoid; the class on the outer wrapper
              fires unreliably in that case, leaving Iron partway down the
              prior page. Iron's title relies on its own padding-top
              (.pdf-section-title) for the page-edge buffer. Only fires when
              BOTH RBC and IRON have markers — otherwise the marker would emit
              a blank page (PDFSection renders nothing when markers.length ===
              0, but the break marker above it would still fire). */}
          {!isCollapsed(Section.RBC) && bloodworkAnalysis.getRBCPanelMarkers().length > 0
            && !isCollapsed(Section.IRON) && bloodworkAnalysis.getIronPanelMarkers().length > 0 && (
            <>
              <div className="html2pdf__page-break" />
            </>
          )}

          {!isCollapsed(Section.IRON) && (
            <div className={sectionClass('IRON')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Iron Status & Utilization"
                section={Section.IRON}
                panelMarkers={bloodworkAnalysis.getIronPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.GLUCOSE) && (
            <>
              {/* Glucose Regulation & Metabolic Function always starts on its
                  own page so its panel and bar graphs sit together below the
                  page header. The title's own padding-top (.pdf-section-title)
                  buffers it from the page edge so html2pdf doesn't clip the
                  top of the title text. */}
              <div className="html2pdf__page-break" />
              <div className={sectionClass('GLUCOSE')}>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Glucose Regulation & Metabolic Function"
                  section={Section.GLUCOSE}
                  panelMarkers={glucoseDisplayMarkers}
                />
              </div>
            </>
          )}
          {/* Empty break marker AFTER Glucose ensures Lipid Balance starts
              at the top of a fresh page. We use a marker rather than a
              break-after class on Glucose's wrapper because in non-prod
              Glucose has a keep-together wrapper around its bar graphs and
              the break-after class fires unreliably in that case (same root
              cause as the WBC/MOO/Iron issues). Lipid's title relies on its
              own padding-top (.pdf-section-title) for the page-edge buffer. */}
          {!isCollapsed(Section.GLUCOSE) && lipidsVisible && (
            <>
              <div className="html2pdf__page-break" />
            </>
          )}

          {!isCollapsed(Section.LIPIDS) && (
            <div className={sectionClass('LIPIDS')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Lipid Balance"
                section={Section.LIPIDS}
                panelMarkers={bloodworkAnalysis.getLipidsPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.THYROID) && (
            <div className={sectionClass('THYROID')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Thyroid Function"
                section={Section.THYROID}
                panelMarkers={bloodworkAnalysis.getThyroidPanelMarkers()}
              />
            </div>
          )}

          {/* Vitamin & Mineral Status (with automatic split after 13 markers) */}
          {mineralMarkers.length > 0 && !isCollapsed(Section.NUTRIENTS) && (
            <>
              <div className={sectionClass('NUTRIENTS_1')}>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Vitamin & Mineral Status"
                  section={Section.NUTRIENTS}
                  panelMarkers={nutrientsFirstPageMarkers}
                />
              </div>

              {nutrientsSecondPageMarkers.length > 0 && (
                <div className={sectionClass('NUTRIENTS_2')}>
                  <PDFSection
                    withNotes={this.state.withNotes}
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title="Vitamin & Mineral Status (cont.)"
                    section={Section.NUTRIENTS}
                    panelMarkers={nutrientsSecondPageMarkers}
                  />
                </div>
              )}
            </>
          )}

          {!isCollapsed(Section.PROTEINS) && (
            <div className={sectionClass('PROTEINS')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Protein Status"
                section={Section.PROTEINS}
                panelMarkers={bloodworkAnalysis.getProteinsPanelMarkers()}
              />
            </div>
          )}

          {hasWbcValue && !isCollapsed(Section.WBC) && (
            // Immune Activity is forced to a fresh page by setting break-after
            // on the last visible pre-WBC pack unit (typically PROTEINS). See
            // the unitBreakAfter override above. To force the FOLLOWING
            // Inflammation section onto its own fresh page, we insert an
            // empty break marker AFTER WBC's content. (Putting the class on
            // WBC's outer wrapper interacts badly with the bar-graphs
            // keep-together avoid inside it, leaving the break in the wrong
            // position.) Markers placed AFTER content are safe — they have
            // zero height and sit at the end of the last page of preceding
            // content, then trigger the page-break for the next element.
            // Inflammation's title relies on its own padding-top
            // (.pdf-section-title) for the page-edge buffer.
            <>
              {/* Immune Activity reaches this fresh page via break-after on the
                  last pre-WBC pack unit (PROTEINS) — the same packer-break
                  mechanism Thyroid / Liver / Kidney / Hormones / Prostate use.
                  Those sections carry NO leading spacer and let the title's own
                  padding-top (.pdf-section-title, 10px) buffer the page edge,
                  and their titles sit flush at the top of the page. The Immune
                  gauge previously had an extra spacer here, which made its title
                  sit noticeably lower than every other section title. A spacer
                  at a page-break boundary is collapsed/absorbed by html2pdf
                  (see pdf.css), so resizing it had no visible effect — the title
                  was always positioned by the break + its own padding. Dropping
                  the spacer entirely lines the title up with the other packer-
                  break sections.

                  All immune markers render in one "Immune Activity Results"
                  panel (see PDFSection's refRangeTitle): the gauge sits on its
                  own page, then the single bar-graph panel — with its own title
                  — follows on the next page. */}
              <div>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Immune Activity"
                  section={Section.WBC}
                  panelMarkers={immuneDisplayMarkers}
                />
              </div>
              <div className="html2pdf__page-break" />
            </>
          )}

          {!isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS) && (
            // Inflammation gets its fresh page via the break-after class on
            // WBC's wrapper above (when WBC is visible). When WBC is hidden,
            // the packer decides whether Inflammation packs with the prior
            // section.
            <div className={sectionClass('INFLAMMATION')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Inflammation & Oxidative Stress"
                section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                panelMarkers={inflammationDisplayMarkers}
              />
            </div>
          )}

          {!isCollapsed(Section.PLATELETS) && (
            <div className={sectionClass('PLATELETS')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Blood Clotting Function"
                section={Section.PLATELETS}
                panelMarkers={bloodworkAnalysis.getPlateletPanelMarkers()}
              />
            </div>
          )}

          {/* Line-space spacer so Liver Function reads as visually separated
              from Blood Clotting Function when they share a page. When Liver
              moves to its own page (because packing pushed it), the spacer
              just sits at the bottom of Blood Clotting's page and is
              invisible. */}
          {!isCollapsed(Section.PLATELETS) && !isCollapsed(Section.LIVER_BILIARY) && (
            <div style={{ height: 20 }} aria-hidden />
          )}

          {!isCollapsed(Section.LIVER_BILIARY) && (
            <div className={sectionClass('LIVER')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Liver Function"
                section={Section.LIVER_BILIARY}
                panelMarkers={bloodworkAnalysis.getLiverPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.KIDNEY) && (
            <div className={sectionClass('KIDNEY')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Kidney Function"
                section={Section.KIDNEY}
                panelMarkers={bloodworkAnalysis.getKidneyPanelMarkers()}
              />
            </div>
          )}

          {/* Line-space spacer so Electrolyte Balance reads as visually
              separated from Kidney Function when they share a page. */}
          {!isCollapsed(Section.KIDNEY) && !isCollapsed(Section.ELECTROLYTES) && (
            <div style={{ height: 20 }} aria-hidden />
          )}

          {!isCollapsed(Section.ELECTROLYTES) && (
            <div className={sectionClass('ELECTROLYTES')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Electrolyte Balance"
                section={Section.ELECTROLYTES}
                panelMarkers={bloodworkAnalysis.getElectrolytePanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.STRESS_ADRENAL) && bloodworkAnalysis.getStressAdrenalPanelMarkers().length > 0 && (
            <div className={sectionClass('STRESS_ADRENAL')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Stress & Adrenal Balance"
                section={Section.STRESS_ADRENAL}
                panelMarkers={bloodworkAnalysis.getStressAdrenalPanelMarkers()}
              />
            </div>
          )}

          {/* Hormones Analysis (with automatic split after 13 markers) */}
          {sexHormoneMarkers.length > 0 && !isCollapsed(Section.SEX_HORMONE) && (
            <>
              <div className={sectionClass('SEX_HORMONE_1')}>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Hormone Balance"
                  section={Section.SEX_HORMONE}
                  panelMarkers={hormonesFirstPageMarkers}
                />
              </div>

              {hormonesSecondPageMarkers.length > 0 && (
                <div className={sectionClass('SEX_HORMONE_2')}>
                  <PDFSection
                    withNotes={this.state.withNotes}
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title="Hormone Balance (cont.)"
                    section={Section.SEX_HORMONE}
                    panelMarkers={hormonesSecondPageMarkers}
                  />
                </div>
              )}
            </>
          )}

          {!isCollapsed(Section.PROSTATE) && (
            <div className={sectionClass('PROSTATE')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Prostate Function"
                section={Section.PROSTATE}
                panelMarkers={bloodworkAnalysis.getProstatePanelMarkers()}
              />
            </div>
          )}

          {/* Global Notes — flows onto last bar-graph page when room remains;
              the break is forced on the last section above when it won't fit. */}
          {this.state.withNotes && globalNotesText && (
            <>
              {!insightsNeedsOwnPage && <div style={{ height: 12 }} />}
              <Card
                className="disclaimer-panel mb-5"
                style={{ whiteSpace: 'pre-line' }}
              >
                <Card.Header className="py-1">Insights</Card.Header>
                <Card.Body
                  className="disclaimer-body"
                  style={{ whiteSpace: 'pre-line', paddingTop: 0 }}
                >
                  {globalNotesText}
                </Card.Body>
              </Card>
            </>
          )}

          {/* Supplements summary — collects each section's Supplements tab
              into one panel styled like the Insights card. Gated on the
              "Include in PDF" checkbox (default-on via the user's Settings)
              and only rendered outside prod/slava, matching the per-section
              Supplements tab visibility. Always starts on a fresh page. */}
          {process.env.REACT_APP_ENV !== 'prod' &&
            process.env.REACT_APP_ENV !== 'slava' &&
            !isCollapsed(Section.SUPPLEMENTS) && (
            <>
              <div className="html2pdf__page-break" />
              {/* Push the panel down from the page edge so the header isn't
                  hugged tight to the top margin or clipped by the page break.
                  Trimmed by one line height per user request. */}
              <div style={{ height: 28 }} aria-hidden />
              {/* Wrap in keep-together so the "Supplements" header stays
                  pinned to its content and does not orphan at the bottom of
                  the prior page when html2pdf is laying out the panel. */}
              <div className="pdf-glucose-bar-graphs">
                <SupplementsSummaryPanel
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  enabledSupplementBrands={
                    this.props.userProfileUIStore?.currentUserProfile?.enabledSupplementBrands
                  }
                  forPdf
                />
              </div>
            </>
          )}

          {/* Disclaimer — forced to its own page when it won't fit after the
              preceding content (Insights, or Supplements when included).
              Extra top spacing pushes it down from the page edge so it doesn't
              hug the header. */}
          {disclaimerNeedsOwnPage && <><div className="html2pdf__page-break" /><div style={{ height: 48 }} /></>}
          <DisclaimerSection furtherConsiderations={furtherConsiderations} />
        </div>
      </div>
      </>
    );
  }
}
